import citations from './source/observatory-citations.json'
import { hasPending, inlineText } from './text'
import { ContentBlock, ContentPage } from './types'

export interface ObservatoryCitation {
  edition: string
  after: string
  sentence: string
}
export const OBSERVATORY_CITATIONS: Record<string, ObservatoryCitation> = citations
export const observatoryPath = (edition: string): string => `/dados/glp-1/${edition}`
export const observatoryEditionLabel = (edition: string): string =>
  new Intl.DateTimeFormat('pt-BR', { month: 'long', year: 'numeric', timeZone: 'UTC' }).format(
    new Date(`${edition}-01T00:00:00Z`)
  )

/** Hide an unresolved atomic block/item, never its editorial placeholder or partial sentence. */
export const visibleBlocks = (blocks: ContentBlock[]): ContentBlock[] =>
  blocks.flatMap((block): ContentBlock[] => {
    if (block.kind === 'cta') return []
    if (block.kind === 'paragraph') return hasPending(block.children) ? [] : [block]
    if (block.kind === 'list') {
      const items = block.items.filter((item) => !hasPending(item))
      return items.length ? [{ ...block, items }] : []
    }
    if (block.header.some(hasPending)) return []
    const rows = block.rows.filter((row) => !row.some(hasPending))
    return rows.length ? [{ ...block, rows }] : []
  })

export const articleSections = (page: ContentPage) =>
  page.sections
    .filter(
      (section) => section.heading && !['Resposta rápida', 'Perguntas frequentes', 'Fontes'].includes(section.heading)
    )
    .map((section) => ({ ...section, blocks: visibleBlocks(section.blocks) }))
    .filter((section) => section.blocks.length)

export const summaryText = (page: ContentPage): string =>
  visibleBlocks(page.sections[0].blocks)
    .map((block) => (block.kind === 'paragraph' ? inlineText(block.children) : ''))
    .filter(Boolean)
    .join('\n')

/** Section boundaries, counted from visible body H2s. Part B's initial/final markers anchor the plan.
 * With 6–8 sections the specified A/B/A fits; a fourth B needs at least 9 sections.
 * For longer articles choose the feasible boundary nearest the midpoint, leaving 3 H2s per gap.
 */
export const ctaBoundaries = (page: ContentPage): number[] => {
  const sections = articleSections(page)
  const count = sections.length
  if (count < 3) return [0]
  const markers = sections.flatMap((section, index) =>
    page.sections.find((source) => source.heading === section.heading)?.blocks.some((block) => block.kind === 'cta')
      ? [index + 1]
      : []
  )
  const positions =
    count <= 5
      ? [0, count]
      : count < 9
      ? [0, Math.floor(count / 2), count]
      : [0, Math.min(Math.floor(count / 2), count - 6), Math.min(Math.floor(count / 2), count - 6) + 3, count]
  // Honor interior markers when they fit the H2 spacing and count constraints.
  for (const marker of markers.filter((position) => position < count)) {
    const index = positions.findIndex(
      (_, i) => i > 0 && i < positions.length - 1 && marker - positions[i - 1] >= 3 && positions[i + 1] - marker >= 3
    )
    if (index >= 0) positions[index] = marker
  }
  return positions
}

export interface ArticleCallout {
  id: 'bom-saber' | 'dica-do-medico' | 'dado-observatorio'
  titleId: string
  title: string
  variant: 'info' | 'tip' | 'data'
  after: string
  text: string
}
export const articleCallouts = (page: ContentPage): ArticleCallout[] => {
  const citation = OBSERVATORY_CITATIONS[page.slug]
  return [
    ...(page.article
      ? [
          {
            id: 'bom-saber' as const,
            titleId: 'bom-saber-titulo',
            title: 'Bom saber',
            variant: 'info' as const,
            ...page.article.info
          },
          {
            id: 'dica-do-medico' as const,
            titleId: 'dica-titulo',
            title: 'Dica do médico',
            variant: 'tip' as const,
            ...page.article.tip
          }
        ]
      : []),
    ...(citation
      ? [
          {
            id: 'dado-observatorio' as const,
            titleId: 'dado-observatorio-titulo',
            title: 'Dado do Observatório Medleve',
            variant: 'data' as const,
            after: citation.after,
            text: citation.sentence
          }
        ]
      : [])
  ].filter(
    (callout) =>
      !!callout.text.trim() &&
      !/\{\{|Dado pendente/.test(callout.text) &&
      articleSections(page).some((section) => section.heading === callout.after)
  )
}
